import { inject } from '@angular/core';
import { CanMatchFn, UrlSegment } from '@angular/router';
import { isReservedSlug } from '../shared/reserved-slugs';
import { SeoPageSlugCacheService } from '../services/seo-page-slug-cache.service';

/**
 * Gate for the root-level `/:slug` SEO-page route.
 *
 * Three outcomes:
 * 1. Slug is reserved → return false, router falls through to the `**`
 *    catch-all (NotFoundComponent).
 * 2. Slug cache is loaded AND slug is not in the whitelist → return false
 *    (same fall-through — avoids mounting the SEO component for a 404).
 * 3. Cache not ready OR cache ready + slug whitelisted → return true and
 *    let `SeoPageComponent` handle the detail fetch (incl. its own 404
 *    + 5xx UI).
 *
 * Runs under `canMatch` so it's consulted per-match, not per-navigation —
 * prevents the brief flash of the SEO shell before the component decides it
 * is a 404.
 */
export const nonReservedSlugGuard: CanMatchFn = (_route, segments: UrlSegment[]) => {
  const slug = segments[0]?.path ?? '';
  if (isReservedSlug(slug)) return false;

  const cache = inject(SeoPageSlugCacheService);
  if (cache.isReady() && !cache.has(slug)) return false;

  return true;
};
